import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PRIMARY_ROLE_TO_COLOR} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {NEAR_BLACK, WHITE} from 'ui-library/Colors';

const labelProps = {dominantBaseline: 'central', textAnchor: 'middle', cx: 0, cy: 0};

interface PrimaryExperimentalRoleSymbolProps {
  radius: number;
  primaryRole: PrimaryExperimentalRoleType;
  label: string | null;
  // Optionally define center position of the symbol
  cx?: number;
  cy?: number;
}

/**
 * Displays an SVG symbol representing a primary role.
 */
export default function PrimaryExperimentalRoleSymbol({
  radius,
  primaryRole,
  label,
  cx,
  cy,
}: PrimaryExperimentalRoleSymbolProps): JSX.Element {
  const svgSize = radius * 2;
  const x = cx ?? radius;
  const y = cy ?? radius;
  const labelSize = Math.min((label?.length ?? 0) * 10, svgSize);

  return (
    <svg className={css(styles.symbolSvg)} width={svgSize} height={svgSize}>
      <g transform={`translate(${x},${y})`}>
        {primaryRole === PrimaryExperimentalRoleType.BLANK ? (
          <>
            <circle cx={0} cy={0} r={radius} fill={WHITE} stroke={NEAR_BLACK} strokeWidth={1} />
            <line
              transform="rotate(45)"
              x1={0}
              y1={-radius}
              x2={0}
              y2={radius}
              stroke={NEAR_BLACK}
              strokeWidth={1}
            />
            {label && (
              <>
                {/* Draw the label with a white background so the line doesn't interfere with the text */}
                <rect x={-labelSize / 2} y={-7} width={labelSize} height={14} fill={WHITE} />
                <text className={css(styles.textLabel)} fill={NEAR_BLACK} {...labelProps}>
                  {label}
                </text>
              </>
            )}
          </>
        ) : (
          <>
            <circle cx={0} cy={0} r={radius} fill={PRIMARY_ROLE_TO_COLOR[primaryRole]} />
            {label && (
              <text className={css(styles.textLabel)} fill={NEAR_BLACK} {...labelProps}>
                {label}
              </text>
            )}
          </>
        )}
      </g>
    </svg>
  );
}

const styles = StyleSheet.create({
  symbolSvg: {overflow: 'visible'},
  textLabel: {cursor: 'inherit'},
});
